import type { CvParseRecovery } from "./onboarding-api";
import { getCvParseRecovery } from "./onboarding-api";
import {
  getResumeHandoffExtractedText,
  isRetryableResumeHandoffClientError,
  ResumeHandoffClientError,
} from "./resume-handoff";
import { isImportedCvIncompleteError } from "./cv-save-sections";

/**
 * Copy resolution for the recoverable CV-import outcomes introduced after the
 * 2026-09-02 incident. One place decides which sentence the user reads, so the
 * onboarding composer (web + native), the resume-handoff auto-import and the
 * post-onboarding import screen agree.
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const CV_PARSE_RECOVERY_FALLBACK_COPY = {
  parseIncomplete:
    "We couldn't find work experience, education, or certifications in this CV, so nothing was changed. Try another file or paste the CV text.",
  parseIncompleteReview:
    "We couldn't read every section of this CV, so nothing was changed. The text we could read is below: review it, add anything missing, and extract again.",
  parseProviderUnavailable:
    "Our CV reader is temporarily unavailable, so nothing was changed. Please try again in a few minutes, or paste the CV text.",
  handoffRetryLater:
    "Your uploaded CV is kept for a short while. Try again in a few minutes, or upload it below.",
} as const;

/**
 * Turn a resume-handoff claim failure into the same recovery shape a direct
 * upload produces, so both paths share one message resolver.
 */
export function getResumeHandoffParseRecovery(
  error: unknown,
): CvParseRecovery | null {
  if (!isRetryableResumeHandoffClientError(error)) return null;
  const handoffError = error as ResumeHandoffClientError;
  const quality = handoffError.meta?.quality;
  return {
    kind:
      handoffError.code === "CV_PROVIDER_UNAVAILABLE"
        ? "provider_unavailable"
        : "incomplete",
    quality:
      quality && typeof quality === "object"
        ? (quality as CvParseRecovery["quality"])
        : null,
    extractedText: getResumeHandoffExtractedText(error),
  };
}

/** Recovery for any import failure: direct upload, pasted text, or handoff. */
export function getAnyCvParseRecovery(error: unknown): CvParseRecovery | null {
  return getCvParseRecovery(error) ?? getResumeHandoffParseRecovery(error);
}

export function resolveCvParseRecoveryMessage(
  t: Translate,
  recovery: CvParseRecovery,
  options: {
    /** The extracted text was placed in the editor for the user to review. */
    reviewOffered: boolean;
  },
) {
  if (recovery.kind === "provider_unavailable") {
    return t("onboarding.cvJourney.parseProviderUnavailable", {
      defaultValue: CV_PARSE_RECOVERY_FALLBACK_COPY.parseProviderUnavailable,
    });
  }
  if (options.reviewOffered) {
    return t("onboarding.cvJourney.parseIncompleteReview", {
      defaultValue: CV_PARSE_RECOVERY_FALLBACK_COPY.parseIncompleteReview,
    });
  }
  return t("onboarding.cvJourney.parseIncomplete", {
    defaultValue: CV_PARSE_RECOVERY_FALLBACK_COPY.parseIncomplete,
  });
}

/**
 * Message for an import the CLIENT refused before any request: the parse had
 * no CV body, so the destructive save was never built.
 */
export function resolveImportedCvIncompleteMessage(t: Translate, error: unknown) {
  if (!isImportedCvIncompleteError(error)) return null;
  return t("onboarding.cvJourney.parseIncomplete", {
    defaultValue: CV_PARSE_RECOVERY_FALLBACK_COPY.parseIncomplete,
  });
}
